import Decimal from 'decimal.js';

/**
 * Money handling for the client (CONVENTIONS.md §3).
 *
 * Money values are NEVER converted to a JS `number`. They live as `string` in
 * every store, prop and API payload. `Decimal` is used only transiently,
 * inside a computation or a formatter, and the result is converted straight
 * back to a `string` before it leaves this module.
 */

Decimal.set({ rounding: Decimal.ROUND_HALF_UP });

export const ZERO = '0.0000';

/** Indian digit grouping: last 3 digits, then groups of 2. 1234567 -> "12,34,567" */
function groupIndian(intPart: string): string {
  const negative = intPart.startsWith('-');
  const digits = negative ? intPart.slice(1) : intPart;
  if (digits.length <= 3) return (negative ? '-' : '') + digits;
  const last3 = digits.slice(-3);
  const rest = digits.slice(0, -3);
  const groups: string[] = [];
  for (let i = rest.length; i > 0; i -= 2) {
    groups.unshift(rest.slice(Math.max(0, i - 2), i));
  }
  return (negative ? '-' : '') + groups.join(',') + ',' + last3;
}

export interface FormatOptions {
  /** Number of decimal places to show. Default 2. */
  decimals?: number;
  /** Show the ₹ symbol. Default true. */
  symbol?: boolean;
  /** Render negative values in accounting parens, e.g. (1,234.00). Default false. */
  parens?: boolean;
}

/** Format a decimal string as Indian-grouped currency: ₹12,34,567.89 */
export function formatIndian(value: string | null | undefined, opts: FormatOptions = {}): string {
  const { decimals = 2, symbol = true, parens = false } = opts;
  if (value === null || value === undefined || value === '') return symbol ? '₹0.00' : '0.00';
  let d: Decimal;
  try {
    d = new Decimal(value);
  } catch {
    return symbol ? '₹0.00' : '0.00';
  }
  const negative = d.isNegative();
  const abs = d.abs().toFixed(decimals);
  const [intPart, fracPart] = abs.split('.');
  const grouped = groupIndian(intPart ?? '0');
  const body = fracPart ? `${grouped}.${fracPart}` : grouped;
  const prefix = symbol ? '₹' : '';
  if (negative && parens) return `(${prefix}${body})`;
  return `${negative ? '-' : ''}${prefix}${body}`;
}

/** Format a quantity/rate: plain international grouping, no currency symbol. */
export function formatQty(value: string | null | undefined, decimals = 2): string {
  if (value === null || value === undefined || value === '') return (0).toFixed(decimals);
  let d: Decimal;
  try {
    d = new Decimal(value);
  } catch {
    return (0).toFixed(decimals);
  }
  const negative = d.isNegative();
  const abs = d.abs().toFixed(decimals);
  const [intPart, fracPart] = abs.split('.');
  const withCommas = (intPart ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const body = fracPart ? `${withCommas}.${fracPart}` : withCommas;
  return `${negative ? '-' : ''}${body}`;
}

/**
 * Parse what an Indian accountant actually types into an amount field:
 *  - Indian grouping "1,23,456.78" or plain "123456.78"
 *  - Leading currency symbol/text "Rs.", "INR", "₹"
 *  - Accounting-style negatives: "(1234)" or "-1234"
 *  - Bare decimals ".5" -> "0.5"
 *  - Trailing/leading whitespace
 * Returns a normalised fixed-point string (4 decimal places, matching the
 * server's NUMERIC(18,4)), or null if the input cannot be parsed as a number.
 */
export function parseAmountInput(raw: string): string | null {
  if (raw == null) return null;
  let s = raw.trim();
  if (s === '') return null;

  let negative = false;
  // Accounting parens: (1234.50)
  const parenMatch = s.match(/^\((.*)\)$/);
  if (parenMatch) {
    negative = true;
    s = parenMatch[1] ?? '';
  }

  // Strip currency markers and any whitespace.
  s = s.replace(/^(rs\.?|inr|₹)\s*/i, '').replace(/\s+/g, '');

  if (s.startsWith('-')) {
    negative = true;
    s = s.slice(1);
  } else if (s.startsWith('+')) {
    s = s.slice(1);
  }

  // Remove thousands separators (commas), Indian or otherwise.
  s = s.replace(/,/g, '');

  if (s.startsWith('.')) s = '0' + s;
  if (s === '') return null;

  if (!/^\d+(\.\d+)?$/.test(s)) return null;

  let d: Decimal;
  try {
    d = new Decimal(s);
  } catch {
    return null;
  }
  if (negative) d = d.negated();
  return d.toFixed(4);
}

/** Add two money strings, returning a money string. */
export function addMoney(a: string, b: string): string {
  return new Decimal(a || 0).plus(new Decimal(b || 0)).toFixed(4);
}

/** Subtract two money strings, returning a money string. */
export function subMoney(a: string, b: string): string {
  return new Decimal(a || 0).minus(new Decimal(b || 0)).toFixed(4);
}

/** True if the string represents zero (avoids float comparison bugs). */
export function isZero(value: string | null | undefined): boolean {
  if (value === null || value === undefined || value === '') return true;
  try {
    return new Decimal(value).isZero();
  } catch {
    return true;
  }
}

/** Compare two money strings: -1, 0, 1 */
export function compareMoney(a: string, b: string): number {
  return new Decimal(a || 0).comparedTo(new Decimal(b || 0));
}
